export interface BotBuddyLocationLike {
  href: string;
  origin: string;
  hostname: string;
}

export function resolveBotBuddyEnvironment(rawEnvironment: string | undefined, mode: string, hostname: string) {
  if (hostname === "127.0.0.1" || hostname === "localhost") {
    return "local";
  }

  if (hostname === "enterprise-staging.vercel.app" || hostname === "staging.supply-guard.io") {
    return "staging";
  }

  if (hostname.endsWith(".vercel.app")) {
    return "preview";
  }

  if (rawEnvironment === "staging") {
    return "staging";
  }

  if (rawEnvironment === "development") {
    return "local";
  }

  return rawEnvironment ?? mode;
}

export function shouldEnableBotBuddyWidget(hostname: string, rawEnvironment: string | undefined) {
  // Hard-block the canonical production host. This is the only
  // user-facing production URL, and it's the surface BotBuddy must
  // never reach.
  if (hostname === "app.supply-guard.io") {
    return false;
  }

  // ENT-1285 codex rounds 1+2 P2: gate by the *resolved* environment,
  // not the raw VITE_SENTRY_ENVIRONMENT.
  //
  // - vars.SENTRY_ENVIRONMENT is sourced as `production` for some
  //   deploys regardless of intent (staging.yml, preview.yml, and
  //   reusable-live-release-deploy.yml all read the same repo var),
  //   so `rawEnvironment === "production"` does NOT reliably mean
  //   "production deploy".
  // - resolveBotBuddyEnvironment encodes the canonical mapping:
  //   localhost → local, enterprise-staging.vercel.app → staging,
  //   any *.vercel.app → preview, then falls back to raw. So an
  //   actual canonical-non-vercel production hostname is the only
  //   path that resolves to "production" outside `app.supply-guard.io`.
  //
  // Trust that mapping. If an internal Vercel-hash URL of a
  // production build is ever reached by a user, the BotBuddy server
  // also enforces the contract on its side (register_totp_seed
  // rejects production with HTTP 400 and the widget suppresses the
  // 2FA chip on `environment="production"`), so layered defence
  // remains.
  const resolvedEnvironment = resolveBotBuddyEnvironment(rawEnvironment, "", hostname);
  if (resolvedEnvironment === "production") {
    return false;
  }

  return true;
}

export function normalizeBotBuddyPublicToken(rawToken: string | undefined | null) {
  const trimmed = rawToken?.trim() ?? "";
  return trimmed.startsWith("public_") ? trimmed : null;
}

// ENT-1285: render whenever the widget is enabled for this hostname /
// environment, regardless of whether a `public_token` was baked into the
// build. The BotBuddy SDK has its own resolution waterfall
// (`testRunToken` → `appSlug + environment + version` → `commitSha` →
// `branchName + prNumber`) and can attach to the right test run without
// an explicit token. The previous AND-gate was overconstrained: any
// preview deploy where the CI step that bakes
// `VITE_BOTBUDDY_TEST_RUN_TOKEN` failed silently hid the widget
// entirely, even though the SDK could still resolve via waterfall.
// The token, when present, is still passed through to the widget as
// the most reliable resolution path — the `testRunToken` argument is
// kept in the signature so callers don't need to change.
//
// Production hostnames are already excluded by `shouldEnableBotBuddyWidget`,
// so relaxing this gate cannot leak the widget to `app.supply-guard.io`
// or any future production host.
export function shouldRenderBotBuddyWidget(showWidget: boolean, _testRunToken: string | null) {
  // ENT-3155: never render the widget in a WebDriver-controlled browser.
  // Its Codex thread deep-link chips otherwise overlay app controls and
  // intercept Playwright pointer events (see isBrowserAutomationEnvironment).
  if (isBrowserAutomationEnvironment()) {
    return false;
  }

  return showWidget;
}

export function shouldSuppressBotBuddyWidgetForPath(pathname: string) {
  return pathname.startsWith("/pitch-book/briefing/");
}

interface BotBuddyNavigatorLike {
  webdriver?: boolean;
}

// ENT-3155: the BotBuddy TestRunWidget renders Codex thread deep-link
// chips (`<a href="codex://threads/<id>">`) under #root when agents are
// registered against the run. In a Playwright preview/E2E session those
// chips can overlay application controls and intercept pointer events — the
// vendor-questionnaire "50%" layout button was visible and enabled but
// timed out on all three retries because an injected codex:// anchor caught
// the click. The owning integration is `@botbuddy/react`'s TestRunWidget,
// mounted via BotBuddyWidgetBridge in App.tsx.
//
// Playwright, Selenium, and every other WebDriver-based automation set
// `navigator.webdriver === true`; real human browsers report `false`. We
// suppress the widget for automated sessions so no deep-link overlay can
// reach a Playwright-driven page, while human preview testers keep it.
export function isBrowserAutomationEnvironment(
  navigatorLike: BotBuddyNavigatorLike | undefined =
    typeof navigator === "undefined" ? undefined : navigator,
) {
  return navigatorLike?.webdriver === true;
}

export function resolveBotBuddyTargetUrl(
  locationLike: BotBuddyLocationLike,
  rawEnvironment: string | undefined,
  mode: string,
) {
  return locationLike.origin;
}
